/**
 * Catálogo de presets: GET /api/presence/presets.php es la fuente principal.
 * Fallback estático (presencePresetI18n) solo si el fetch falla o aún no hubo intento de carga.
 * No se mezclan listas endpoint + estática en la misma validación.
 */

import { API_ENDPOINTS } from '../utils/constants';
import {
  PRESET_SHORT_KEYS,
  PRESET_SHORT_TO_I18N_KEY,
  isValidPresetShortKey,
} from '../utils/presencePresetI18n.js';

/** @type {'idle' | 'endpoint' | 'fallback'} */
let catalogMode = 'idle';

/** @type {Array<{ key: string, i18n_key: string, default_emoji: string }>|null} */
let cachedPresets = null;

/** @type {Set<string>|null} */
let validKeySet = null;

/** @type {Promise<Array<{ key: string, i18n_key: string, default_emoji: string }>>|null} */
let inflightLoad = null;

function buildStaticPresetsList() {
  return PRESET_SHORT_KEYS.map((key) => ({
    key,
    i18n_key: PRESET_SHORT_TO_I18N_KEY[key],
    default_emoji: '',
  }));
}

function applyStaticFallback() {
  catalogMode = 'fallback';
  cachedPresets = buildStaticPresetsList();
  validKeySet = new Set(PRESET_SHORT_KEYS);
}

/**
 * @returns {Promise<Array<{ key: string, i18n_key: string, default_emoji: string }>>}
 */
export async function loadPresencePresets() {
  if (catalogMode === 'endpoint' && cachedPresets) {
    return cachedPresets;
  }
  if (catalogMode === 'fallback' && cachedPresets) {
    return cachedPresets;
  }
  if (inflightLoad) {
    return inflightLoad;
  }
  inflightLoad = (async () => {
    try {
      const url = API_ENDPOINTS.PRESENCE_PRESETS || '/api/presence/presets.php';
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`presets_http_${res.status}`);
      }
      const data = await res.json();
      if (!data.success || !Array.isArray(data.presets) || data.presets.length === 0) {
        throw new Error('presets_invalid_payload');
      }
      catalogMode = 'endpoint';
      cachedPresets = data.presets;
      validKeySet = new Set(data.presets.map((p) => p.key));
      return cachedPresets;
    } catch {
      applyStaticFallback();
      return /** @type {Array<{ key: string, i18n_key: string, default_emoji: string }>} */ (cachedPresets);
    } finally {
      inflightLoad = null;
    }
  })();
  return inflightLoad;
}

/**
 * @returns {Array<{ key: string, i18n_key: string, default_emoji: string }>|null}
 */
export function getCachedPresencePresets() {
  return cachedPresets;
}

/**
 * @returns {Set<string>|null}
 */
export function getRegistryPresetKeySet() {
  return validKeySet;
}

/**
 * Modo actual del catálogo (telemetría / tests).
 * @returns {'idle' | 'endpoint' | 'fallback'}
 */
export function getPresenceCatalogMode() {
  return catalogMode;
}

/**
 * i18n_key para un preset: prioriza fila del catálogo cacheado (endpoint o fallback), luego mapa estático.
 *
 * @param {string} shortKey
 * @returns {string|null}
 */
export function getPresetI18nKeyForResolver(shortKey) {
  if (cachedPresets && cachedPresets.length) {
    const row = cachedPresets.find((p) => p.key === shortKey);
    if (row?.i18n_key) {
      return row.i18n_key;
    }
  }
  return PRESET_SHORT_TO_I18N_KEY[shortKey] || null;
}

/**
 * Valida contra un único origen: tras carga, solo el Set del catálogo (endpoint o fallback);
 * en idle, lista estática hasta el primer loadPresencePresets().
 *
 * @param {unknown} key
 * @returns {boolean}
 */
export function isPresetKeyValidInRegistry(key) {
  if (typeof key !== 'string') return false;
  const k = key.trim();
  if (!k) return false;
  if (validKeySet && validKeySet.size > 0) {
    return validKeySet.has(k);
  }
  return isValidPresetShortKey(k);
}
